import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Listing } from '../types';
import { useCurrency } from '../context/CurrencyContext';
import { 
  ShieldCheck, Zap, Star, Clock, CheckCircle2, 
  Heart, Share2, AlertCircle, ShoppingCart, MessageSquare, ArrowLeft 
} from 'lucide-react';
import { VerifiedSellerBadge, WarrantyBadge, InstantDeliveryBadge } from '../components/common/Badge';

export const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { displayPrice } = useCurrency();

  const [listing, setListing] = useState<Listing | null>(null);
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchListing = async () => {
      try {
        const res = await fetch(`/api/listings/${id}`);
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.listing) {
            setListing(data.listing);
            if (data.listing.images && data.listing.images.length > 0) {
              setSelectedImage(data.listing.images[0]);
            }
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchListing();
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center text-sm font-semibold">
        Loading listing details...
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-bold">Listing Not Found</h2>
        <Link to="/browse" className="mt-4 inline-block text-brand-blue font-bold">Return to Catalog</Link>
      </div>
    );
  }

  const priceInfo = displayPrice(listing.price_cents * quantity);

  const handleBuyNow = () => {
    navigate('/checkout', {
      state: {
        listing_id: listing.id,
        quantity: quantity
      }
    });
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-10">
      
      {/* Breadcrumbs */}
      <div className="flex items-center gap-2 text-xs text-brand-lightSecText dark:text-brand-darkSecText">
        <Link to="/" className="hover:text-brand-blue">Home</Link>
        <span>/</span>
        <Link to={`/games/${listing.game_slug}`} className="hover:text-brand-blue">{listing.game_name}</Link>
        <span>/</span>
        <span className="uppercase">{listing.category_slug}</span>
        <span>/</span>
        <span className="text-brand-lightText dark:text-brand-darkText font-semibold truncate max-w-xs">{listing.title}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        
        {/* Left 2 Cols: Media & Detailed Specs */}
        <div className="lg:col-span-2 space-y-8">
          
          {/* Main Gallery Image */}
          <div className="rounded-3xl overflow-hidden bg-slate-900 border border-brand-lightBorder dark:border-brand-darkBorder aspect-[16/10] relative">
            <img
              src={selectedImage || 'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=1000'}
              alt={listing.title}
              className="w-full h-full object-cover"
            />
            {listing.delivery_type === 'automatic' && (
              <div className="absolute top-4 left-4">
                <InstantDeliveryBadge />
              </div>
            )}
          </div>

          {/* Thumbnail Strip */}
          {listing.images && listing.images.length > 1 && (
            <div className="flex items-center gap-3 overflow-x-auto pb-2">
              {listing.images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedImage(img)}
                  className={`w-20 h-14 rounded-xl overflow-hidden border-2 transition-all flex-shrink-0 ${
                    selectedImage === img ? 'border-brand-blue scale-105' : 'border-transparent opacity-70 hover:opacity-100'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          {/* Dynamic Attributes Grid */}
          {listing.attributes && Object.keys(listing.attributes).length > 0 && (
            <div className="p-6 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder">
              <h3 className="font-extrabold text-base text-brand-lightText dark:text-brand-darkText mb-4">
                Item Specifications & Verification
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {Object.entries(listing.attributes).map(([key, val]) => (
                  <div key={key} className="p-3 rounded-2xl bg-brand-lightSec dark:bg-brand-darkElevated border border-brand-lightBorder dark:border-brand-darkBorder">
                    <span className="text-[11px] font-semibold text-brand-lightSecText dark:text-brand-darkSecText uppercase tracking-wider block">
                      {key.replace('_', ' ')}
                    </span>
                    <span className="font-bold text-sm text-brand-lightText dark:text-brand-darkText mt-0.5 block truncate">
                      {String(val)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          <div className="p-6 sm:p-8 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder space-y-4">
            <h3 className="font-extrabold text-base text-brand-lightText dark:text-brand-darkText">
              Seller Description
            </h3>
            <p className="text-sm text-brand-lightText dark:text-brand-darkText leading-relaxed whitespace-pre-line">
              {listing.description}
            </p>
          </div>

          {/* Verified Customer Reviews */}
          <div className="p-6 sm:p-8 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-extrabold text-base text-brand-lightText dark:text-brand-darkText">
                  Verified Reviews ({listing.reviews?.length || 0})
                </h3>
                <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText">
                  Only buyers who completed verified orders can leave feedback
                </p>
              </div>
              <div className="flex items-center gap-1.5 text-amber-500 font-bold text-sm">
                <Star className="w-4 h-4 fill-current" />
                <span>{listing.seller_rating || 5.0} / 5.0</span>
              </div>
            </div>

            {listing.reviews && listing.reviews.length > 0 ? (
              <div className="space-y-4 divide-y divide-brand-lightBorder dark:divide-brand-darkBorder">
                {listing.reviews.map((rev) => (
                  <div key={rev.id} className="pt-4 first:pt-0 space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-brand-lightText dark:text-brand-darkText">
                          {rev.buyer_username || 'Verified Customer'}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-500 font-semibold">
                          Verified Purchase
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-amber-500 font-bold">
                        <Star className="w-3.5 h-3.5 fill-current" />
                        <span>{rev.rating}.0</span>
                      </div>
                    </div>
                    <p className="text-xs text-brand-lightText dark:text-brand-darkText leading-relaxed">
                      "{rev.comment}"
                    </p>
                    {rev.seller_reply && (
                      <div className="p-3 rounded-xl bg-brand-lightSec dark:bg-brand-darkElevated border border-brand-lightBorder dark:border-brand-darkBorder text-xs mt-2">
                        <span className="font-bold text-brand-blue dark:text-brand-accent block text-[11px] mb-1">
                          Seller Reply:
                        </span>
                        <p className="text-brand-lightSecText dark:text-brand-darkSecText">{rev.seller_reply}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-xs text-brand-lightSecText dark:text-brand-darkSecText">
                No reviews yet for this listing. Be the first to purchase and review!
              </div>
            )}
          </div>
        </div>

        {/* Right Col: Purchase Box & Seller Info */}
        <div className="space-y-6">
          
          {/* Purchase Action Box */}
          <div className="p-6 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder shadow-xl space-y-6">
            <div>
              <span className="text-xs uppercase font-extrabold tracking-wider text-brand-blue dark:text-brand-accent">
                {listing.game_name}
              </span>
              <h1 className="text-xl font-black text-brand-lightText dark:text-brand-darkText mt-1">
                {listing.title}
              </h1>
            </div>

            {/* Price Header */}
            <div className="pt-2 pb-4 border-b border-brand-lightBorder dark:border-brand-darkBorder flex items-baseline justify-between">
              <div>
                <span className="font-black text-3xl text-brand-blue dark:text-brand-accent">
                  {priceInfo.formatted}
                </span>
                <span className="text-xs text-brand-lightSecText dark:text-brand-darkSecText block mt-0.5">
                  Charged: {priceInfo.chargedUsd}
                </span>
              </div>
              <WarrantyBadge hours={listing.warranty_hours || 48} />
            </div>

            {/* Quantity Selector if multiple in stock */}
            {listing.stock_count > 1 && (
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-brand-lightText dark:text-brand-darkText">Quantity:</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-8 h-8 rounded-lg border border-brand-lightBorder dark:border-brand-darkBorder flex items-center justify-center font-bold text-sm"
                  >
                    -
                  </button>
                  <span className="font-bold w-6 text-center">{quantity}</span>
                  <button
                    onClick={() => setQuantity(Math.min(listing.stock_count, quantity + 1))}
                    className="w-8 h-8 rounded-lg border border-brand-lightBorder dark:border-brand-darkBorder flex items-center justify-center font-bold text-sm"
                  >
                    +
                  </button>
                </div>
              </div>
            )}

            {/* Delivery Specifications */}
            <div className="space-y-2.5 text-xs text-brand-lightSecText dark:text-brand-darkSecText">
              <div className="flex items-center justify-between">
                <span>Delivery Method:</span>
                <strong className="text-brand-lightText dark:text-brand-darkText capitalize">
                  {listing.delivery_type === 'automatic' ? 'Instant Automatic Reveal' : 'Manual Transfer via Seller'}
                </strong>
              </div>
              <div className="flex items-center justify-between">
                <span>Estimated Speed:</span>
                <strong className="text-emerald-600 dark:text-emerald-400">
                  {listing.delivery_type === 'automatic' ? 'Instant (< 1 minute)' : `~${listing.delivery_time_minutes} minutes`}
                </strong>
              </div>
              <div className="flex items-center justify-between">
                <span>Stock Status:</span>
                <strong className={listing.stock_count > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-500'}>
                  {listing.stock_count > 0 ? `${listing.stock_count} Available` : 'Sold Out'}
                </strong>
              </div>
            </div>

            {/* Buy Now Button */}
            <button
              onClick={handleBuyNow}
              disabled={listing.stock_count <= 0}
              className="w-full py-4 rounded-2xl font-extrabold text-sm text-white bg-brand-blue hover:bg-brand-hover shadow-glow-blue flex items-center justify-center gap-2 transition-all disabled:opacity-50"
            >
              <ShoppingCart className="w-4 h-4" />
              <span>{listing.stock_count > 0 ? 'Buy Now with Buyer Protection' : 'Sold Out'}</span>
            </button>

            {/* Share and wishlist */}
            <div className="flex items-center justify-center gap-6 pt-2 text-xs font-semibold text-brand-lightSecText dark:text-brand-darkSecText">
              <button onClick={handleShare} className="flex items-center gap-1.5 hover:text-brand-blue transition-colors">
                <Share2 className="w-3.5 h-3.5" />
                <span>{copied ? 'Link Copied!' : 'Share Offer'}</span>
              </button>
            </div>
          </div>

          {/* Seller Profile Summary Card */}
          <div className="p-6 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder space-y-4">
            <h4 className="font-extrabold text-xs uppercase tracking-wider text-brand-lightSecText dark:text-brand-darkSecText">
              Merchant Information
            </h4>

            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-brand-blue/20 text-brand-blue flex items-center justify-center font-bold text-lg">
                {listing.store_name?.charAt(0) || 'S'}
              </div>
              <div>
                <Link to={`/seller/${listing.seller_id}`} className="font-bold text-sm text-brand-lightText dark:text-brand-darkText hover:text-brand-blue">
                  {listing.store_name || 'Verified Vendor'}
                </Link>
                <div className="flex items-center gap-1.5 text-xs text-amber-500 font-bold">
                  <Star className="w-3 h-3 fill-current" />
                  <span>{listing.seller_rating || 5.0}</span>
                  <span className="text-brand-lightSecText font-normal">
                    ({listing.seller_reviews || 0} reviews)
                  </span>
                </div>
              </div>
            </div>

            <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText line-clamp-3">
              {listing.seller_bio || 'Professional game vendor with verified identity and proven trade track record on GameVault.'}
            </p>

            <div className="pt-2 border-t border-brand-lightBorder dark:border-brand-darkBorder text-xs text-brand-lightSecText space-y-1">
              <p>Working Hours: <strong className="text-brand-lightText dark:text-brand-darkText">{listing.working_hours || '24/7 Rapid'}</strong></p>
              <p>Total Completed: <strong className="text-brand-lightText dark:text-brand-darkText">{listing.seller_orders || 1400}+ orders</strong></p>
            </div>

            <Link
              to={`/seller/${listing.seller_id}`}
              className="w-full block text-center py-2.5 rounded-xl border border-brand-lightBorder dark:border-brand-darkBorder text-xs font-bold text-brand-lightText dark:text-brand-darkText hover:bg-brand-lightSec dark:hover:bg-brand-darkElevated transition-colors"
            >
              View Seller Store & Catalog
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
